/*
  Cart upsell ("Complete Your Mission") component. Used by both the cart
  drawer and the cart page.

  - <cart-upsell-slider> initializes a Swiper instance and survives the cart
    re-renders (innerHTML swaps re-connect the element).
  - Clicks on .cart-upsell__add are handled via document-level delegation so
    the handler keeps working after any cart section re-render. The added
    product refreshes the sections of whichever cart component (cart-drawer-items
    or cart-items) contains the button, using the same section-rendering flow
    as cart.js.
*/

(() => {
  const SWIPER_OPTIONS = {
    slidesPerView: 'auto',
    spaceBetween: 12,
  };

  class CartUpsellSlider extends HTMLElement {
    connectedCallback() {
      this.initAttempts = 0;
      this.initSwiper();
    }

    disconnectedCallback() {
      if (this.swiper) {
        this.swiper.destroy(true, false);
        this.swiper = null;
      }
    }

    initSwiper() {
      if (!this.isConnected || this.swiper) return;

      if (typeof Swiper === 'undefined') {
        if (this.initAttempts < 50) {
          this.initAttempts += 1;
          setTimeout(() => this.initSwiper(), 100);
        }
        return;
      }

      this.swiper = new Swiper(this, SWIPER_OPTIONS);
    }
  }

  if (!customElements.get('cart-upsell-slider')) {
    customElements.define('cart-upsell-slider', CartUpsellSlider);
  }

  const getSectionInnerHTML = (html, selector) =>
    new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;

  const showError = (message) => {
    const errors = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (errors) errors.textContent = message;
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.cart-upsell__add');
    if (!button || button.disabled) return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    // The upsell block always lives inside a cart component; its
    // getSectionsToRender() knows which sections this page needs refreshed.
    const cartComponent = button.closest('cart-drawer-items, cart-items');
    const sectionsToRender = cartComponent ? cartComponent.getSectionsToRender() : [];

    button.disabled = true;
    button.classList.add('loading');

    const body = JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
      sections: sectionsToRender.map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status) {
          showError(parsedState.description || window.cartStrings.error);
          return;
        }

        sectionsToRender.forEach((section) => {
          const base = document.getElementById(section.id);
          if (!base) return;
          const target = (section.selector && base.querySelector(section.selector)) || base;
          target.innerHTML = getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        });

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-upsell',
            cartData: parsedState,
            variantId: variantId,
          });
        }
      })
      .catch((error) => {
        console.error(error);
        showError(window.cartStrings.error);
      })
      .finally(() => {
        // The re-render usually replaces the button; reset it in case it is
        // still in the DOM (e.g. after an error).
        button.disabled = false;
        button.classList.remove('loading');
      });
  });
})();
